/**
 * O modal "Registrar pagamento", idêntico ao do admin atual (`admin.html:2906` a `2960` e `7840` a
 * `8127`; linhas 86 a 98 do inventário).
 *
 * NO LEGADO ele abre por cima do Kanban, quando o card vai para "Pagamento a receber". No sistema
 * novo a Vendas abre `/financeiro?registrar=<id do lead>`, e a tela do Financeiro desenha este
 * modal. "Cancelar" volta para Vendas, onde o card continua na etapa que tinha, e "Confirmar" fica
 * no Financeiro recarregado, que é o que o legado faz ao clicar a aba Financeiro (`admin.html:8126`).
 *
 * O ACHADO 2 do inventário (seção 4) está reproduzido e isolado na constante
 * `ACHADO_2_NAO_APLICAR_CUPOM_ESCONDE_SEM_DESCONTO`.
 */

// Estado e efeito do React.
import { useEffect, useState } from 'react';
// As peças do Chassi.
import { Botao, Campo, CampoTexto, Selecao, Selo } from '@complexity/ui';
// As linhas do banco.
import type { LinhaGiftCards, LinhaLeadsFunil } from '@studiolu/dados';
// As chamadas do modal, cada uma com a linha do legado.
import {
  apagarParcelasDoPagamento,
  atualizarPagamento,
  inserirPagamento,
  inserirParcelas,
  lerPagamentoDoLead,
  lerResgatePendenteDoLead,
  listarPrecosDosProdutos,
  marcarCupomComoAplicado,
  mudarStatusDoLead,
} from '@studiolu/dados/areas/financeiro';
// A fonte de dados do painel.
import { usePainel } from '../../contexto.js';
// Moeda, modal e formatos do legado.
import { formatarMoeda } from '../../comum/formatos.js';
import { mascararMoedaLegado, parseMoeda } from '../../comum/moeda.js';
import { ModalLegado } from '../../comum/modal-legado.js';
// O catálogo de produtos, porte do legado que a ficha da cliente já tem (conferido com `admin.html:3076` e `8746`).
import { LEAD_PRODUTO_TO_PARAM, PRODUTOS_LEAD } from '../ficha-cliente/comum-ficha/catalogo.js';
// As regras puras: parcelas e formas.
import { OPCOES_FORMA, calcularParcelas, dataDoCampo } from './regras.js';
// A linha compacta de campos.
import { LinhaCompacta } from './apoio.js';

/**
 * ACHADO 2, reproduzido por decisão do fundador: o "Não aplicar" do cupom testa se a faixa do gift
 * card está com `display === 'block'`, mas ela é `flex` (`admin.html:7972` x `7952`), então o teste
 * dá sempre falso e o "Valor total sem desconto" some MESMO com gift card aplicado. `false` corrige.
 */
const ACHADO_2_NAO_APLICAR_CUPOM_ESCONDE_SEM_DESCONTO = true;

// O cupom da cliente, como o modal o mostra.
type Cupom =
  | { readonly aplicado: true; readonly texto: string; readonly resgateId: number; readonly valorAplicado: number }
  | { readonly aplicado: false; readonly texto: string };

export function RegistrarPagamento({
  lead,
  giftCards,
  aoCancelar,
  aoConcluir,
}: {
  readonly lead: LinhaLeadsFunil;
  // Os gift cards carregados; o modal abate os que este lead resgatou.
  readonly giftCards: readonly LinhaGiftCards[];
  readonly aoCancelar: () => void;
  readonly aoConcluir: () => void;
}) {
  const { dados: fonte } = usePainel();
  // Os campos, que abrem limpos como `abrirModalPagamento` deixa (`admin.html:7857` a `7869`).
  const [valor, setValor] = useState('');
  const [qtd, setQtd] = useState('1');
  const [entrada, setEntrada] = useState('');
  // `true` assim que a pessoa digita na Entrada: a partir daí ela não é mais preenchida sozinha.
  const [entradaManual, setEntradaManual] = useState(false);
  const [data, setData] = useState('');
  const [forma, setForma] = useState('pix');
  const [observacao, setObservacao] = useState('');
  // Os selos dos produtos, o cupom, o gift card e a referência sem desconto.
  const [produtos, setProdutos] = useState<string[]>([]);
  const [cupom, setCupom] = useState<Cupom | null>(null);
  const [faixaCupom, setFaixaCupom] = useState(false);
  const [giftCardAplicado, setGiftCardAplicado] = useState(0);
  const [faixaGiftCard, setFaixaGiftCard] = useState(false);
  const [textoGiftCard, setTextoGiftCard] = useState('');
  const [semDesconto, setSemDesconto] = useState('');
  // A linha de status embaixo, e o botão travado enquanto grava.
  const [status, setStatus] = useState('');
  const [salvando, setSalvando] = useState(false);

  // A abertura: preço dos produtos, cupom pendente e gift cards, nesta ordem (`admin.html:7872` a `7961`).
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const chaves = lead.produtos ?? [];
      // Os gift cards resgatados por ESTE lead, comparados estritamente como no legado.
      const doLead = giftCards.filter((gc) => gc.lead_id_resgate === lead.id);
      const valorGiftCards = doLead.reduce((s, gc) => s + Number(gc.valor), 0);
      // As chaves de preço dos produtos, sem repetir.
      const paramKeys = [...new Set(chaves.map((p) => LEAD_PRODUTO_TO_PARAM[p]).filter((p): p is string => !!p))];
      const precoPorProduto: Record<string, number> = {};
      let total = '';
      if (paramKeys.length > 0) {
        const precos = await listarPrecosDosProdutos(fonte, paramKeys);
        // Erro ou nenhum preço: o total fica vazio, como o `if (!error && precos && precos.length > 0)`.
        if (precos.ok && precos.dados && precos.dados.length > 0) {
          precos.dados.forEach((r) => {
            precoPorProduto[r.produto] = Number(r.preco) || 0;
          });
          const soma = precos.dados.reduce((s, r) => s + (Number(r.preco) || 0), 0);
          if (soma > 0) total = formatarMoeda(soma);
        }
      }
      if (!vivo) return;
      // "Produto · R$ X" quando há preço; só o nome quando não há.
      setProdutos(
        chaves.map((p) => {
          const chave = LEAD_PRODUTO_TO_PARAM[p];
          const preco = chave ? precoPorProduto[chave] : undefined;
          return `${PRODUTOS_LEAD[p] || p}${preco !== undefined ? ` · ${formatarMoeda(preco)}` : ''}`;
        }),
      );
      // O valor antes de qualquer desconto, a referência do "sem desconto".
      const valorAntes = parseMoeda(total);
      // O cupom que a cliente informou no portal; o legado ignora o erro desta leitura (`7912`).
      const resgate = await lerResgatePendenteDoLead(fonte, lead.id);
      if (!vivo) return;
      const pendente = resgate.ok ? resgate.dados?.cupons_desconto : null;
      let aplicado: Cupom | null = null;
      if (pendente) {
        // Vencido só depois do fim do dia da validade.
        const expirado = !!pendente.validade && new Date(pendente.validade + 'T23:59:59') < new Date();
        if (!pendente.ativo || expirado) {
          aplicado = {
            aplicado: false,
            texto: `Cupom ${pendente.codigo} informado pela cliente, mas está ${!pendente.ativo ? 'desativado' : 'expirado'}. Não foi aplicado.`,
          };
        } else {
          // Percentual sobre o valor sem desconto; fixo pelo valor; o total nunca fica negativo.
          const desconto =
            pendente.tipo_desconto === 'percentual'
              ? valorAntes * (Number(pendente.valor_desconto) / 100)
              : Number(pendente.valor_desconto);
          total = formatarMoeda(Math.max(0, valorAntes - desconto));
          aplicado = {
            aplicado: true,
            texto: `Cupom ${pendente.codigo} informado pela cliente, aplicado automaticamente: -${formatarMoeda(desconto)}`,
            resgateId: resgate.ok && resgate.dados ? resgate.dados.id : 0,
            valorAplicado: desconto,
          };
        }
        setCupom(aplicado);
        setFaixaCupom(true);
      }
      // Os gift cards, DEPOIS do cupom, sobre o valor já com cupom.
      if (doLead.length) {
        const antes = parseMoeda(total);
        total = formatarMoeda(Math.max(0, antes - valorGiftCards));
        setGiftCardAplicado(valorGiftCards);
        const codigos = doLead.map((gc) => gc.codigo).join(', ');
        setTextoGiftCard(
          `Gift Card${doLead.length > 1 ? 's' : ''} ${codigos}, aplicado automaticamente: -${formatarMoeda(valorGiftCards)}`,
        );
        setFaixaGiftCard(true);
      }
      // "Sem desconto" só com cupom APLICADO ou gift card em jogo.
      if (doLead.length || aplicado?.aplicado) setSemDesconto(`Valor total sem desconto: ${formatarMoeda(valorAntes)}`);
      setValor(total);
    })();
    return () => {
      vivo = false;
    };
    // A abertura roda uma vez por lead, como `abrirModalPagamento`.
  }, [fonte, lead, giftCards]);

  /**
   * A Entrada preenchida sozinha com a divisão igual (`atualizarPreviewParcelas`, `admin.html:8024`
   * a `8035`), enquanto a pessoa não digitar nela. O legado faz isto a cada digitação em valor,
   * parcelas, entrada e data, e nos dois "Não aplicar"; aqui cada um desses gestos chama esta função.
   */
  function entradaAutomatica(novoValor: string, novaQtd: string, manual: boolean): void {
    const q = parseInt(novaQtd, 10) || 1;
    const t = parseMoeda(novoValor);
    if (q > 1 && t > 0 && !manual) setEntrada(formatarMoeda(Math.round((t / q) * 100) / 100));
  }

  // A prévia, calculada do que está nos campos, como o legado a redesenha a cada digitação.
  const total = parseMoeda(valor);
  const quantidade = parseInt(qtd, 10) || 1;
  const previa = total && data ? calcularParcelas(total, quantidade, parseMoeda(entrada), dataDoCampo(data)) : [];
  const totalCalculado = previa.reduce((s, p) => s + p.valor, 0);

  // "Não aplicar" do cupom: devolve o desconto e esconde a faixa (`admin.html:7964`).
  function naoAplicarCupom() {
    if (!cupom || !cupom.aplicado) return;
    const novo = formatarMoeda(parseMoeda(valor) + cupom.valorAplicado);
    setValor(novo);
    entradaAutomatica(novo, qtd, entradaManual);
    setCupom(null);
    setFaixaCupom(false);
    // Achado 2: o teste do legado nunca enxerga a faixa do gift card.
    const temGiftCard = ACHADO_2_NAO_APLICAR_CUPOM_ESCONDE_SEM_DESCONTO ? false : faixaGiftCard;
    if (!temGiftCard) setSemDesconto('');
  }

  // "Não aplicar" do gift card: devolve o valor e esconde a faixa (`admin.html:7976`).
  function naoAplicarGiftCard() {
    if (!giftCardAplicado) return;
    const novo = formatarMoeda(parseMoeda(valor) + giftCardAplicado);
    setValor(novo);
    entradaAutomatica(novo, qtd, entradaManual);
    setGiftCardAplicado(0);
    setFaixaGiftCard(false);
    // Sem gift card, "sem desconto" só continua se ainda houver cupom aplicado.
    if (!cupom?.aplicado) setSemDesconto('');
  }

  // "Confirmar" (`admin.html:8075` a `8126`): valida nesta ordem, grava e move o lead.
  async function confirmar() {
    const t = parseMoeda(valor);
    const q = parseInt(qtd, 10) || 1;
    // À vista, a entrada é o total.
    const e = q > 1 ? parseMoeda(entrada) : t;
    const obs = observacao.trim();
    if (!t || t <= 0) return setStatus('Informe o valor total.');
    if (!data) return setStatus('Informe a data da 1ª parcela.');
    if (q > 1 && (!e || e <= 0)) return setStatus('Informe o valor da entrada.');
    if (q > 1 && e >= t) return setStatus('A entrada não pode ser igual ou maior que o valor total.');
    setSalvando(true);
    setStatus('Salvando...');
    const campos = { valor_total: t, forma_pagamento: forma, observacao: obs || null };
    // Plano existente é ATUALIZADO e as parcelas antigas apagadas; senão, insere.
    const existente = await lerPagamentoDoLead(fonte, lead.id);
    let pagamentoId: number;
    if (existente.ok && existente.dados) {
      const r = await atualizarPagamento(fonte, existente.dados.id, campos, new Date().toISOString());
      if (!r.ok) {
        setStatus('Erro: ' + r.erro.mensagem);
        return setSalvando(false);
      }
      pagamentoId = existente.dados.id;
      // O legado não confere o erro desta limpeza.
      await apagarParcelasDoPagamento(fonte, pagamentoId);
    } else {
      const novo = await inserirPagamento(fonte, lead.id, campos);
      if (!novo.ok) {
        setStatus('Erro: ' + novo.erro.mensagem);
        return setSalvando(false);
      }
      pagamentoId = novo.dados.id;
    }
    const lista = calcularParcelas(t, q, e, dataDoCampo(data));
    const parcelas = await inserirParcelas(
      fonte,
      lista.map((p) => ({
        pagamento_id: pagamentoId,
        numero: p.numero,
        // Duas casas só aqui, na gravação, como o `parseFloat(p.valor.toFixed(2))` do legado.
        valor: parseFloat(p.valor.toFixed(2)),
        // A data local convertida por `toISOString`, como o legado (`admin.html:8110`).
        data_vencimento: p.data.toISOString().slice(0, 10),
      })),
    );
    if (!parcelas.ok) {
      setStatus('Erro ao salvar parcelas: ' + parcelas.erro.mensagem);
      return setSalvando(false);
    }
    const movido = await mudarStatusDoLead(fonte, lead.id, 'pagamento_a_receber');
    if (!movido.ok) {
      setStatus('Erro ao mover lead: ' + movido.erro.mensagem);
      return setSalvando(false);
    }
    // O cupom pendente vira aplicado, sem conferir o erro, como o legado.
    if (cupom?.aplicado) await marcarCupomComoAplicado(fonte, cupom.resgateId, cupom.valorAplicado, new Date().toISOString());
    aoConcluir();
  }

  return (
    <ModalLegado
      aberto
      aoFechar={aoCancelar}
      titulo="Registrar pagamento"
      testid="modal-registrar-pagamento"
      rodape={
        <>
          <Botao variante="secundario" onClick={aoCancelar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void confirmar()} disabled={salvando}>
            Confirmar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-cx-3">
        {/* O nome do lead no subtítulo. */}
        <p className="text-texto-suave">{lead.nome || ''}</p>
        {/* Os produtos com o preço padrão. */}
        {produtos.length > 0 && (
          <div className="flex flex-wrap gap-cx-2" data-testid="pag-produtos">
            {produtos.map((texto, i) => (
              <Selo key={i}>{texto}</Selo>
            ))}
          </div>
        )}
        {/* A faixa do gift card fica no topo, e a do cupom embaixo dela, como no legado. */}
        {faixaGiftCard && (
          <div className="flex flex-wrap items-center gap-cx-2" data-testid="pag-faixa-giftcard">
            <span className="text-sucesso">{textoGiftCard}</span>
            <Botao variante="texto" tamanho="sm" onClick={naoAplicarGiftCard}>
              Não aplicar
            </Botao>
          </div>
        )}
        {faixaCupom && cupom && (
          <div className="flex flex-wrap items-center gap-cx-2" data-testid="pag-faixa-cupom">
            <span className={cupom.aplicado ? 'text-sucesso' : 'text-erro'}>{cupom.texto}</span>
            {/* Cupom recusado não tem "Não aplicar": não há desconto a devolver. */}
            {cupom.aplicado && (
              <Botao variante="texto" tamanho="sm" onClick={naoAplicarCupom}>
                Não aplicar
              </Botao>
            )}
          </div>
        )}
        {semDesconto && (
          <p className="text-cx-sm text-texto-suave" data-testid="pag-sem-desconto">
            {semDesconto}
          </p>
        )}
        <LinhaCompacta>
          <Campo
            rotulo="Valor total"
            placeholder="R$ 0,00"
            inputMode="numeric"
            value={valor}
            onChange={(ev) => {
              const novo = mascararMoedaLegado(ev.target.value);
              setValor(novo);
              entradaAutomatica(novo, qtd, entradaManual);
            }}
          />
          <Campo
            rotulo="Parcelas"
            type="number"
            min={1}
            max={48}
            value={qtd}
            onChange={(ev) => {
              setQtd(ev.target.value);
              entradaAutomatica(valor, ev.target.value, entradaManual);
            }}
          />
          {/* A Entrada só com mais de uma parcela. */}
          {quantidade > 1 && (
            <Campo
              rotulo="Entrada (1ª parcela)"
              placeholder="R$ 0,00"
              inputMode="numeric"
              value={entrada}
              onChange={(ev) => {
                // Digitou na Entrada: ela deixa de ser automática antes de qualquer recálculo.
                setEntradaManual(true);
                setEntrada(mascararMoedaLegado(ev.target.value));
              }}
            />
          )}
          <Campo
            rotulo="Data da 1ª parcela"
            type="date"
            value={data}
            onChange={(ev) => {
              setData(ev.target.value);
              entradaAutomatica(valor, qtd, entradaManual);
            }}
          />
          <Selecao
            rotulo="Forma de pagamento"
            opcoes={OPCOES_FORMA}
            value={forma}
            onChange={(ev) => setForma(ev.target.value)}
          />
        </LinhaCompacta>
        <CampoTexto
          rotulo="Observação"
          placeholder="Informações adicionais sobre o pagamento..."
          linhas={3}
          value={observacao}
          onChange={(ev) => setObservacao(ev.target.value)}
        />
        {/* A prévia das parcelas: só com valor e data. */}
        {previa.length > 0 && (
          <div data-testid="pag-previa">
            <p className="text-cx-sm font-medium text-texto">Parcelas</p>
            {previa.map((p) => (
              <div key={p.numero} className="flex gap-cx-4 text-cx-sm tabular-nums">
                <span className="text-texto-fraco">
                  {p.numero}/{quantidade}
                </span>
                <span>{formatarMoeda(p.valor)}</span>
                <span>{p.data.toLocaleDateString('pt-BR')}</span>
              </div>
            ))}
            {/* Aviso só quando a soma se afasta mais de 2 centavos do total. */}
            {Math.abs(totalCalculado - total) > 0.02 && (
              <p className="mt-cx-2 text-cx-xs text-texto-fraco">
                Total calculado: {formatarMoeda(totalCalculado)} (diferença de centavos por arredondamento)
              </p>
            )}
          </div>
        )}
        <p className="min-h-[1.25rem] text-cx-sm text-erro" role="status" data-testid="pag-status">
          {status}
        </p>
      </div>
    </ModalLegado>
  );
}
